import React, { FC, useEffect, useMemo } from 'react';

import { FormikProps } from 'formik';
import { Box } from '@mui/system';
import { Grid } from '@mui/material';

import {
  HelpTypes,
  pendingRefundFilter,
  PendingRequestTabTypes,
  searchedPharmacies,
  TypeOfUser,
} from '@pharmaplan/common';

import { dropdownMapper } from '@pharmaplan/common/helpers/utils';
import useAutoCompleteScroll, {
  AutoCompleteScrollTypes,
} from '../../../../hooks/useAutoCompleteScroll';

import strings from '../../../../localization';
import PendingTabs from '../../PendingTabs';
import CustomDateRange from '../../../common/CustomDateRange';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import FilterSubmitButtons from '../../../common/FilterSubmitButtons';
import { getDisabledSubmitFilter } from '../../../../selectors/calendarSelector';
import CustomAutocomplete from '../../../common/CustomAutocomplete';
import { toOptions } from '../../ExpressBooking/ExpressBooking';
import CustomDropdown from '../../../common/CustomDropdown';

import styles from './styles';
import useAutoComplete from '../../../../hooks/useAutocomplete';

interface IPendingRefundHeader {
  formik: FormikProps<any>;
  setSelected: React.Dispatch<React.SetStateAction<PendingRequestTabTypes>>;
  selectedTab: PendingRequestTabTypes;
  handleReset: () => void;
}

const { pendingRefunds } = PendingRequestTabTypes;

const PendingRefundHeader: FC<IPendingRefundHeader> = ({
  formik,
  selectedTab,
  setSelected,
  handleReset,
}) => {
  const filter = useAppSelector(pendingRefundFilter);
  const { statuses } = filter ?? {};
  const statusMap = useMemo(
    () =>
      statuses.map((item) =>
        ({ key: `${item.key}`, value: item.value })),
    [statuses],
  );

  const disableSubmit = useAppSelector(getDisabledSubmitFilter);
  const pharmacies = useAppSelector(searchedPharmacies);
  const option = toOptions(pharmacies);

  const { debouncedInput: pharmacyInput } = useAutoComplete({
    userTypes: [HelpTypes.pharmacy],
  });

  const { onEndReached, pharmacyListData } = useAutoCompleteScroll(
    AutoCompleteScrollTypes.pharmacy,
  );

  const { pharmacies: formikPharmacies } = formik.values;
  const pharmacyOption = formikPharmacies ? option : pharmacyListData;

  const handleTabClick = (tab: PendingRequestTabTypes) => {
    setSelected(tab);
  };

  const tabConfig = [
    {
      key: pendingRefunds,
      isDefault: true,
      tabName: strings.pendingRefunds,
      onClick: handleTabClick,
    },
  ];

  useEffect(() => {
    pharmacyInput(formikPharmacies);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikPharmacies]);

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <PendingTabs
        selectedTab={selectedTab}
        tabConfig={tabConfig}
        header={strings.pharmagapRequests}
      />
      <Grid container lg={10} alignItems="center" xs={12} wrap="nowrap">
        <CustomDateRange
          startName="startDate"
          endName="endDate"
          formik={formik}
          preventPast={false}
        />
        <Grid xs={6}>
          <CustomDropdown
            label={strings.status}
            formik={formik}
            name="status"
            menuItems={dropdownMapper(statusMap, 'key', 'value')}
          />
        </Grid>
        <Grid xs={6} sx={styles.autocomplete}>
          <CustomAutocomplete
            formik={formik}
            options={pharmacyOption}
            // customAutoCompleteStyle={styles.autocomplete}
            textName="pharmacies"
            autoCompleteName="pharmacySelected"
            idName="pharmacyId"
            label={strings.user}
            placeholder={
              strings.formatString(
                strings.enterCred,
                `${strings.name}/${strings.email}`,
              ) as string
            }
            infiniteScroll={!formikPharmacies}
            onEndReached={onEndReached(TypeOfUser.pharmacy)}
          />
        </Grid>

        <FilterSubmitButtons
          handleReset={handleReset}
          disableSubmit={disableSubmit}
        />
      </Grid>
    </Box>
  );
};

export default PendingRefundHeader;
